import { Redirect, Router } from "@reach/router"
import { useLDFlags } from "@shared/integration/launchdarkly"
import { ScrollToTop } from "@shared/primitive-components"
import { useMemo } from "react"
import { PATHS } from "./const/paths"
import { InsightsProvider, useAuth, usePolicyAndFleet } from "./contexts"
import { Documents } from "./documents"
import { FleetRouter } from "./fleet/FleetRouter"
import { usePolicyAccessCapabilities } from "./hooks"
import { Customers } from "./integration"
import { Billing } from "./integration/Billing"
import { Claims } from "./integration/Claims"
import { PolicyDocuments } from "./integration/PolicyDocuments"
import { RebatesV2 } from "./integration/RebatesV2"
import { Settings } from "./settings"
import { Mileage, RebatesSummary, SafetyInsightsRouter } from "./ui/pages"

export const AuthenticatedRouter = () => {
  const { policyHasAccessTo, isRebateV2Policy } = usePolicyAccessCapabilities()
  const { getAuthType } = useAuth()
  const { policy } = usePolicyAndFleet()
  const { useCharges } = useLDFlags()
  // @TODO: Remove after one year (Remove on 2024-09-26)
  const isRebatesAndCOI =
    policy.productType === `rebates` && Boolean(policy.coiDocumentUrl) && Boolean(policy.effectiveSchedule.documentUrl)
  const isFlocker = useMemo(() => getAuthType() === `admin`, [getAuthType])

  return (
    <Router>
      <ScrollToTop path="/">
        <InsightsProvider path="/">
          <FleetRouter default path="fleet/" />

          {policyHasAccessTo(`REBATES`) &&
            (isRebateV2Policy ? (
              <>
                <RebatesV2 path={PATHS.REBATES} />
                <RebatesV2 path={`policy/:policyId/${PATHS.REBATES}`} />
              </>
            ) : (
              <>
                <RebatesSummary path={PATHS.REBATES} />
                <RebatesSummary path={`policy/:policyId/${PATHS.REBATES}`} />
              </>
            ))}
          {policyHasAccessTo(`CLAIMS`) && (
            <>
              <Claims path={PATHS.CLAIMS} />
              <Claims path={`policy/:policyId/${PATHS.CLAIMS}`} />
            </>
          )}
          {policyHasAccessTo(`MILEAGE`) && (
            <>
              <Mileage path={PATHS.MILEAGE} />
              <Mileage path={`policy/:policyId/${PATHS.MILEAGE}`} />
            </>
          )}
          {policyHasAccessTo(`INSIGHTS`) && (
            <>
              <SafetyInsightsRouter path={`${PATHS.INSIGHTS.BASE}/*`} />
              <SafetyInsightsRouter path={`policy/:policyId/${PATHS.INSIGHTS.BASE}/*`} />
            </>
          )}
          {policyHasAccessTo(`DOCUMENTS`) &&
            (isRebatesAndCOI ? (
              <>
                <PolicyDocuments path="policy/:policyId/documents" />
                <PolicyDocuments path="documents" />
              </>
            ) : (
              <>
                <Documents path="policy/:policyId/documents" />
                <Documents path="documents" />
              </>
            ))}
          {policyHasAccessTo(`BILLING`) && useCharges && (
            <>
              <Billing path={PATHS.BILLING} />
              <Billing path={`policy/:policyId/${PATHS.BILLING}`} />
            </>
          )}

          <Settings path="policy/:policyId/settings" />
          <Settings path="settings" />
          <Redirect from="/policy/:policyId/profile" to="/policy/:policyId/settings" noThrow />
          <Redirect from="/profile" to="/settings" noThrow />

          <FleetRouter path="policy/:policyId/*" />
          {isFlocker && <Customers path="policy/:policyId/customers" />}
        </InsightsProvider>
      </ScrollToTop>
    </Router>
  )
}
